import { getQueueMetrics } from './queue-metrics';
import { Button } from '@/components/v1/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/v1/ui/dialog';
import { Input } from '@/components/v1/ui/input';
import { Loading } from '@/components/v1/ui/loading';
import {
  CopyIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
} from '@radix-ui/react-icons';
import { useMemo, useState } from 'react';

type QueueMetricsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  queueMetrics: object;
  isLoading: boolean;
};

const MetricRows = ({
  rows,
}: {
  rows: ReturnType<typeof getQueueMetrics>['rows'];
}) => (
  <div className="space-y-2">
    {rows.map((row) => (
      <div
        key={row.key}
        className="rounded-md border border-border/70 bg-muted/20 px-3 py-2.5"
      >
        <div className="flex items-center gap-4">
          <span
            className="min-w-0 flex-1 truncate font-mono text-xs text-foreground"
            title={row.key}
          >
            {row.name}
          </span>
          <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
            {row.count.toLocaleString('en-US')}
          </span>
        </div>
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-foreground/35"
            style={{ width: `${row.width}%` }}
          />
        </div>
      </div>
    ))}
  </div>
);

const MetricsEmptyState = ({ search }: { search: string }) => (
  <div className="flex min-h-52 flex-col items-center justify-center rounded-md border border-dashed border-border/70 px-6 text-center">
    <p className="text-sm font-medium text-foreground">
      {search ? 'No matching queues' : 'No queued runs'}
    </p>
    <p className="mt-1 max-w-xs text-xs leading-5 text-muted-foreground">
      {search
        ? 'Try a different queue name.'
        : 'Queues with pending runs will appear here.'}
    </p>
  </div>
);

export function QueueMetricsDialog(props: QueueMetricsDialogProps) {
  const [search, setSearch] = useState('');
  const metrics = useMemo(
    () => getQueueMetrics(props.queueMetrics),
    [props.queueMetrics],
  );
  const query = search.trim().toLocaleLowerCase();
  const rows = metrics.rows.filter((row) =>
    row.key.toLocaleLowerCase().includes(query),
  );

  const setOpen = (open: boolean) => {
    if (!open) {
      setSearch('');
    }
    props.onOpenChange(open);
  };

  return (
    <Dialog open={props.open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="queue-metrics-dialog"
        className="max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[680px] gap-0 overflow-hidden border-border/80 bg-background p-0 shadow-2xl"
      >
        <DialogHeader className="border-b border-border/70 p-5 pr-16 text-left">
          <DialogTitle>Queue Metrics</DialogTitle>
          <DialogDescription>
            Pending runs grouped by their full queue key.
          </DialogDescription>
        </DialogHeader>

        <DialogClose asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 size-8 text-muted-foreground"
            aria-label="Close queue metrics"
          >
            <Cross2Icon className="size-4" />
          </Button>
        </DialogClose>

        <div className="border-b border-border/70 px-5 py-4">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Total queued
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
            {props.isLoading ? '—' : metrics.total.toLocaleString('en-US')}
          </p>
        </div>

        <div className="px-5 py-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search queues..."
              aria-label="Search queue metrics"
              className="bg-muted/15 pl-9"
              disabled={props.isLoading}
            />
          </div>
        </div>

        <div className="min-h-0 max-h-[min(420px,48dvh)] overflow-y-auto px-5 pb-5">
          {props.isLoading ? (
            <Loading className="min-h-52 items-center justify-center" />
          ) : rows.length > 0 ? (
            <MetricRows rows={rows} />
          ) : (
            <MetricsEmptyState search={search} />
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border/70 bg-muted/10 px-5 py-3">
          <span className="text-xs text-muted-foreground">
            {metrics.rows.length.toLocaleString('en-US')}{' '}
            {metrics.rows.length === 1 ? 'queue' : 'queues'}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2 bg-transparent"
            disabled={props.isLoading}
            onClick={() =>
              navigator.clipboard.writeText(
                JSON.stringify(props.queueMetrics, null, 2),
              )
            }
          >
            <CopyIcon className="size-3.5" />
            Copy JSON
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
